/**
 * SPRINT V3 — Challenge a Friend.
 * Lightweight local ledger of challenges the user has sent / accepted.
 * Used to drive the "Reta a un amigo" viral loop without touching the
 * Prediction Engine or Supabase. The actual link is shared externally
 * (WhatsApp / native share) and carries ?ref + ?challenge=<id>.
 */
const KEY = "ss2026_v3_challenges";

export interface ChallengeRecord {
  id: string;
  matchId: string | null;
  prediction: string; // free-form, e.g. "MEX 2-1 ARG"
  createdAt: number;
  status: "sent" | "accepted" | "won" | "lost";
}

interface ChallengeState {
  sent: ChallengeRecord[];
  acceptedIds: string[];
}

function read(): ChallengeState {
  if (typeof window === "undefined") return { sent: [], acceptedIds: [] };
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return { sent: [], acceptedIds: [] };
    return JSON.parse(raw) as ChallengeState;
  } catch {
    return { sent: [], acceptedIds: [] };
  }
}
function write(s: ChallengeState) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* noop */ }
}

function genId(): string {
  return Math.random().toString(36).slice(2, 9).toUpperCase();
}

export function listChallenges(): ChallengeRecord[] {
  return read().sent.slice(-10).reverse();
}

export function createChallenge(opts: { matchId: string | null; prediction: string }): ChallengeRecord {
  const s = read();
  const rec: ChallengeRecord = {
    id: genId(),
    matchId: opts.matchId,
    prediction: opts.prediction.slice(0, 40),
    createdAt: Date.now(),
    status: "sent",
  };
  s.sent.push(rec);
  write(s);
  return rec;
}

export function markChallengeAccepted(id: string) {
  const s = read();
  if (!s.acceptedIds.includes(id)) s.acceptedIds.push(id);
  const rec = s.sent.find((c) => c.id === id);
  if (rec) rec.status = "accepted";
  write(s);
}

export function challengeStats(): { sent: number; accepted: number } {
  const s = read();
  return { sent: s.sent.length, accepted: s.sent.filter((c) => c.status !== "sent").length };
}
